/**
 * Centralized Error Handler
 * Processes and formats API errors for user-friendly display
 */

/**
 * Error types for categorization
 */
export const ErrorTypes = {
  NETWORK: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED_ERROR',
  FORBIDDEN: 'FORBIDDEN_ERROR',
  NOT_FOUND: 'NOT_FOUND_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
};

/**
 * Get user-friendly error message based on error type
 */
const getErrorMessage = (error) => {
  // Network/Connection errors
  if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
    return {
      type: ErrorTypes.NETWORK,
      title: 'Connection Error',
      message: 'Unable to connect to the server. Please check your internet connection and try again.',
      canRetry: true,
    };
  }

  // Timeout errors
  if (error.name === 'AbortError' || error.message.includes('timeout')) {
    return {
      type: ErrorTypes.TIMEOUT,
      title: 'Request Timeout',
      message: 'The request took too long to complete. Please try again.',
      canRetry: true,
    };
  }

  // HTTP Status Code errors
  if (error.status) {
    switch (error.status) {
      case 400:
        return {
          type: ErrorTypes.VALIDATION,
          title: 'Invalid Request',
          message: error.data?.message || 'The request contains invalid data. Please check and try again.',
          canRetry: false,
        };

      case 401:
        return {
          type: ErrorTypes.UNAUTHORIZED,
          title: 'Authentication Required',
          message: 'Your session has expired. Please log in again.',
          canRetry: false,
        };

      case 403:
        return {
          type: ErrorTypes.FORBIDDEN,
          title: 'Access Denied',
          message: 'You do not have permission to access this resource.',
          canRetry: false,
        };

      case 404:
        return {
          type: ErrorTypes.NOT_FOUND,
          title: 'Not Found',
          message: error.data?.message || 'The requested resource was not found.',
          canRetry: false,
        };

      case 429:
        return {
          type: ErrorTypes.SERVER,
          title: 'Too Many Requests',
          message: 'You have made too many requests. Please wait a moment and try again.',
          canRetry: true,
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          type: ErrorTypes.SERVER,
          title: 'Server Error',
          message: 'The server encountered an error. Please try again later.',
          canRetry: true,
        };

      default:
        return {
          type: ErrorTypes.UNKNOWN,
          title: 'Error',
          message: error.data?.message || `An error occurred (${error.status}). Please try again.`,
          canRetry: true,
        };
    }
  }

  // Generic error fallback
  return {
    type: ErrorTypes.UNKNOWN,
    title: 'Unexpected Error',
    message: error.message || 'An unexpected error occurred. Please try again.',
    canRetry: true,
  };
};

/**
 * Main error handler function
 * Processes errors and returns user-friendly information
 *
 * @param {Error} error - Error object from API call
 * @param {Object} context - Additional context for error logging
 * @returns {Object} - Formatted error information
 */
export const handleApiError = (error, context = {}) => {
  const errorInfo = getErrorMessage(error);

  // Log in development mode (suppress 401 errors as they're expected when not authenticated)
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_ENABLE_CONSOLE_LOGS === 'true') {
    // Don't log 401 errors - they're expected when user is not authenticated
    if (errorInfo.type !== ErrorTypes.UNAUTHORIZED) {
      console.group(`[API Error - ${context.module || 'Unknown Module'}]`);
      console.error('Error Type:', errorInfo.type);
      console.error('Error Title:', errorInfo.title);
      console.error('Error Message:', errorInfo.message);
      console.error('Context:', context);
      console.error('Original Error:', error);
      console.groupEnd();
    }
  }

  // Log to external service in production (e.g., Sentry, LogRocket)
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_SENTRY_DSN) {
    // TODO: Integrate with error tracking service
    // Sentry.captureException(error, { contexts: { api: context } });
  }

  return {
    ...errorInfo,
    context,
    originalError: error,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Create error notification object for UI display
 *
 * @param {Object} errorInfo - Error information from handleApiError
 * @returns {Object} - Notification object
 */
export const createErrorNotification = (errorInfo) => {
  return {
    id: `error-${Date.now()}`,
    type: 'error',
    title: errorInfo.title,
    message: errorInfo.message,
    canRetry: errorInfo.canRetry,
    timestamp: errorInfo.timestamp,
  };
};

/**
 * Check if error is retriable
 *
 * @param {Error} error - Error object
 * @returns {boolean} - Whether error can be retried
 */
export const isRetriableError = (error) => {
  const errorInfo = getErrorMessage(error);
  return errorInfo.canRetry;
};

/**
 * Format validation errors from API (usually for form submissions)
 *
 * @param {Object} errorData - Error data from API response
 * @returns {Object} - Field-level error messages
 */
export const formatValidationErrors = (errorData) => {
  if (!errorData || !errorData.errors) {
    return {};
  }

  // Handle different validation error formats
  const errors = {};

  if (Array.isArray(errorData.errors)) {
    // Format: [{ field: 'email', message: 'Invalid email' }, ...]
    errorData.errors.forEach((err) => {
      if (err.field) {
        errors[err.field] = err.message;
      }
    });
  } else if (typeof errorData.errors === 'object') {
    // Format: { email: 'Invalid email', password: 'Too short', ... }
    Object.keys(errorData.errors).forEach((field) => {
      errors[field] = errorData.errors[field];
    });
  }

  return errors;
};

/**
 * Error boundary error handler
 * For React Error Boundaries to use
 *
 * @param {Error} error - Error caught by boundary
 * @param {Object} errorInfo - React error info
 */
export const handleComponentError = (error, errorInfo) => {
  console.error('[Component Error]', error, errorInfo);

  // Log to external service in production
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_SENTRY_DSN) {
    // TODO: Integrate with error tracking service
    // Sentry.captureException(error, { contexts: { react: errorInfo } });
  }
};

export default handleApiError;
